import { useEffect, useState } from 'react';
import { Page, Content } from '@backstage/core-components';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { Grid, Typography, Box, Chip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { GettingStartedCard } from './GettingStartedCard';
import { MyServicesCard } from './MyServicesCard';
import { QuickLaunchCard } from './QuickLaunchCard';
import { RecentActivityCard } from './RecentActivityCard';
import { PlatformHealthCard } from './PlatformHealthCard';
import { PolicyComplianceCard } from './PolicyComplianceCard';
import { ActiveAgentsCard } from './ActiveAgentsCard';
import { StatCardsRow } from './StatCardsRow';
import { MyIssuesCard } from './MyIssuesCard';
import { MyPullRequestsCard } from './MyPullRequestsCard';
import { RecentCommitsCard } from './RecentCommitsCard';
import { NotificationsCard } from './NotificationsCard';
import { useRoleContext } from '../GlobalHeader';

const useStyles = makeStyles(theme => ({
  hero: {
    padding: theme.spacing(4, 0, 3),
  },
  heroTitle: {
    fontSize: '1.75rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    letterSpacing: '-0.02em',
    lineHeight: 1.2,
  },
  heroSubtitle: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    letterSpacing: '0.02em',
    marginTop: theme.spacing(1),
    fontWeight: 400,
  },
  aiBadge: {
    background: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)',
    color: '#fff',
    fontWeight: 600,
    fontSize: '0.65rem',
    letterSpacing: '0.04em',
    height: 22,
    borderRadius: 6,
    marginBottom: theme.spacing(1),
  },
  section: {
    marginTop: theme.spacing(5),
  },
  sectionLabel: {
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontSize: '0.7rem',
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },
}));

export const HomePage = () => {
  const classes = useStyles();
  const identityApi = useApi(identityApiRef);
  const { isAdmin, isPlatformLead, activeRole } = useRoleContext();
  const [userEntityRef, setUserEntityRef] = useState<string | undefined>();

  useEffect(() => {
    identityApi.getBackstageIdentity().then(id => {
      setUserEntityRef(id.userEntityRef);
    }).catch(() => {});
  }, [identityApi]);

  return (
    <Page themeId="home">
      <Content>
        {/* ── Hero Banner ───────────────────────────────────────────── */}
        <Box className={classes.hero}>
          <Chip label="AI-Powered" className={classes.aiBadge} size="small" />
          <Typography className={classes.heroTitle}>Kyndryl Platform Engineering</Typography>
          <Typography className={classes.heroSubtitle}>
            Design · Build · Deliver · Operate · Optimize — your enterprise platform, end to end.
          </Typography>
        </Box>

        {/* ── Stat Cards ────────────────────────────────────────────── */}
        <StatCardsRow />

        {/* ── Your Workspace ────────────────────────────────────────── */}
        <div className={classes.section}>
          <Typography className={classes.sectionLabel}>
            Your Workspace
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <MyServicesCard userEntityRef={userEntityRef} />
            </Grid>
            <Grid item xs={12} md={5}>
              <QuickLaunchCard />
            </Grid>
            <Grid item xs={12} md={8}>
              <RecentActivityCard userEntityRef={userEntityRef} />
            </Grid>
            <Grid item xs={12} md={4}>
              <NotificationsCard />
            </Grid>
            <Grid item xs={12} md={4}>
              <GettingStartedCard userEntityRef={userEntityRef} />
            </Grid>
          </Grid>
        </div>

        {/* ── Platform Lead: Issues, PRs, Commits ───────────────────── */}
        {isPlatformLead && (
          <div className={classes.section}>
            <Typography className={classes.sectionLabel}>
              GitHub Activity
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <MyIssuesCard />
              </Grid>
              <Grid item xs={12} md={4}>
                <MyPullRequestsCard />
              </Grid>
              <Grid item xs={12} md={4}>
                <RecentCommitsCard />
              </Grid>
            </Grid>
          </div>
        )}

        {/* ── Developer: Assigned PRs ───────────────────────────────── */}
        {!isPlatformLead && activeRole?.groupName === 'platform-engineering' && (
          <div className={classes.section}>
            <Typography className={classes.sectionLabel}>
              GitHub Activity
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <MyIssuesCard />
              </Grid>
              <Grid item xs={12} md={6}>
                <MyPullRequestsCard mode="assigned" />
              </Grid>
            </Grid>
          </div>
        )}

        {/* ── Platform Operations ───────────────────────────────────── */}
        {isAdmin && (
          <div className={classes.section}>
            <Typography className={classes.sectionLabel}>
              Platform Operations
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <PlatformHealthCard />
              </Grid>
              <Grid item xs={12} md={4}>
                <ActiveAgentsCard />
              </Grid>
              <Grid item xs={12} md={4}>
                <PolicyComplianceCard />
              </Grid>
            </Grid>
          </div>
        )}
      </Content>
    </Page>
  );
};
